import { defineStore } from "pinia";
import { useSolarPotentialStore } from "@/stores/solarPotential";
import { useSessionStore } from "@/stores/session";
import * as constants from "~/lib/constants";

const consumersValueMapping = {
  consumersHeating: 'E-Heizung',
  consumersHeatPump: 'Wärmepumpe',
  consumersAirConditioner: 'Klimaanlage',
  consumersElectricCar: 'E-Auto',
}

export const useFormStore = defineStore("form", {
  persist: true,
  state: () => {
    return {
      addressData: {
        route: "", // street
        street_number: null,
        locality: null, // city
        country: 'Deutschland',
        postal_code: null,
        latitude: null,
        longitude: null,
      },

      salutation: '', // ['required', 'string', Rule::in(['Mr.', 'Ms.', 'Dr.', 'Prof.'])]
      gender: '', // ['required', 'string', Rule::in(['Male', 'Female', 'Nonbinary', 'Not listed'])]
      first_name: null, // ['required', 'string', 'max:255']
      last_name: null, // ['required', 'string', 'max:255']
      email: null, // ['required', 'email', 'max:255']
      phone: null, // ['required', 'string', 'max:255']
      street: null, // ['required', 'string', 'max:255']
      postal_code: null, // ['required', 'string', 'max:255']
      city: null, // ['required', 'string', 'max:255']
      voucher: null,

      building_type: "Ein- und Zweifamilienhaus", // ['required', 'string', Rule::in(['Ein- und Zweifamilienhaus', 'Reihenhaus', 'Doppelhaushälfte', 'Mehrfamilienhaus', 'Sonstiges'])]
      roof_shape: "Flachdach", // ['required', 'string', Rule::in(['Satteldach', 'Pultdach', 'Walmdach', 'Flachdach', 'Sonstiges'])]
      power_consumption: 4000, // ['required', 'numeric']

      planned_consumers: [], // ['array'] contains ['string', Rule::in(['E-Auto', 'E-Heizung', 'Klimaanlage', 'Wärmepumpe'])]
      installed_consumers: [], // ['array'] contains ['string', Rule::in(['E-Auto', 'E-Heizung', 'Klimaanlage', 'Wärmepumpe'])]

      battery_storage_planned: true, // ['required', 'boolean']

      personsInHousehold: 4000, // used to calculate power consumption on client side

      consumersHeatingOption: false,
      consumersHeatPumpOption: false,
      consumersAirConditionerOption: false,
      consumersElectricCarOption: false,

      consumersHeating: null,
      consumersHeatPump: null,
      consumersAirConditioner: null,
      consumersElectricCar: null,

      building_insight_id: null, // ['required', 'integer']

      square_meters: 150,

      // Value in watts per square meter.
      // Possible values: 0.1, 0.065, 0.055, 0.04
      // Example: 1970 -> 100W/sqm 1971-1995 -> 60-70W/sqm 1995-2000 -> 50-60W/sqm From 2001 -> 40W/sqm
      construction_year: constants.HEATING_PERFORMANCE_BEFORE_1970,

      lead_id: null,
    };
  },

  actions: {
    async saveFormData() {
      // save form data
      const { $api } = useNuxtApp();

      try {
        const response = await $api('leads/create', {
          method: 'POST',
          body: {
            "salutation": this.salutation,
            "gender": this.gender,
            "first_name": this.first_name,
            "last_name": this.last_name,
            "email": this.email,
            "phone": this.phone,
            "street": this.street,
            "postal_code": this.postal_code,
            "city": this.city,
            "building_type": this.building_type,
            "roof_shape": this.roof_shape,
            "planned_consumers": this.planned_consumers,
            "installed_consumers": this.installed_consumers,
            "battery_storage_planned": this.battery_storage_planned,
            "power_consumption": this.power_consumption,
            "voucher": this.voucher,
            "building_insight_id": this.building_insight_id,
          }
        });

        const sessionStore = useSessionStore();
        sessionStore.lead_id = response.id;
        this.lead_id = response.id;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchBuildingInsight() {
      // fetch building insight
      const { $api } = useNuxtApp();
      const solarPotentialStore = useSolarPotentialStore();

      try {
        const response = await $api('building-insights/find-closest', {
          method: 'POST',
          body: {
            "route": this.addressData.route,
            "street_number": this.addressData.street_number,
            "postal_code": this.addressData.postal_code,
            "locality": this.addressData.locality,
            "country": this.addressData.country,
          }
        });

        this.building_insight_id = response.id;
        solarPotentialStore.setSolarPanelConfigs(response.filtered_solar_panels);
        // this.filtered_solar_panels = response.filtered_solar_panels
      } catch (error) {
        console.error(error);
      }
    },

    setConsumersAction(option, value) {
      const result = consumersValueMapping[option];
      const solarPotentialStore = useSolarPotentialStore();

      if (value === 'planned') {
        this.planned_consumers.push(result);
        this.installed_consumers = this.installed_consumers.filter((consumer) => consumer !== result);
      } else if (value === 'installed') {
        this.installed_consumers.push(result);
        this.planned_consumers = this.planned_consumers.filter((consumer) => consumer !== result);
      } else {
        this.planned_consumers = this.planned_consumers.filter((consumer) => consumer !== result);
        this.installed_consumers = this.installed_consumers.filter((consumer) => consumer !== result);
      }

      this[option] = value;

      solarPotentialStore.updateConsumptionOnConsumersChange();
    },

    setOptionAction(option, value) {
      if (value && this[option] === null) {
        this.setConsumersAction(option, 'planned');
      } else if (!value) {
        this.setConsumersAction(option, null);
      }

      this[`${option}Option`] = value;
    },

    resetForm() {
      this.addressData = {
        route: "", // street
        street_number: null,
        locality: null, // city
        country: 'Deutschland',
        postal_code: null,
        latitude: null,
        longitude: null,
      };

      this.salutation = '';
      this.gender = '';
      this.first_name = null;
      this.last_name = null;
      this.email = null;
      this.phone = null;
      this.street = null;
      this.postal_code = null;
      this.city = null;
      this.voucher = null;
      this.building_type = "Ein- und Zweifamilienhaus";
      this.roof_shape = "Flachdach";
      this.power_consumption = 2500;
      this.planned_consumers = [];
      this.installed_consumers = [];
      this.battery_storage_planned = true;
      this.consumersHeatingOption = false;
      this.consumersHeatPumpOption = false;
      this.consumersAirConditionerOption = false;
      this.consumersElectricCarOption = false;
      this.consumersHeating = null;
      this.consumersHeatPump = null;
      this.consumersAirConditioner = null;
      this.consumersElectricCar = null;
      this.building_insight_id = null;
      this.square_meters = 150;
      this.construction_year = constants.HEATING_PERFORMANCE_BEFORE_1970;
      this.lead_id = null;

      const solarPotentialStore = useSolarPotentialStore();
      solarPotentialStore.resetSolarPotential();

      const sessionStore = useSessionStore();
      sessionStore.lead_id = null;
    }
  }
});
